import axios from "axios";
import { FILE_UPLOAD_CONTENT_TYPES, FILE_UPLOAD_MAX_SIZE_BYTES, STATUS_CODES } from "constants/index";
import { FILES_UPLOADS_URL, getFileConfirmUrl } from "constants/urls";
import type { ConfirmedFile, FileDownloadResponse, FileUploadRequest, FileUploadResponse } from "types";
import { axiosGet, axiosPost } from "./index";

const storageClient = axios.create({ withCredentials: false });

function validateFile(file: File) {
  if (!file.name.trim() || file.name.trim().length > 255) {
    throw new Error("El nombre del archivo debe tener entre 1 y 255 caracteres.");
  }
  if (!FILE_UPLOAD_CONTENT_TYPES.some((type) => type === file.type)) {
    throw new Error("Solo se permiten archivos PDF, JPEG o PNG.");
  }
  if (!Number.isSafeInteger(file.size) || file.size <= 0 || file.size > FILE_UPLOAD_MAX_SIZE_BYTES) {
    throw new Error("El archivo debe tener un tamaño mayor a cero y no superar los 10 MiB.");
  }
}

function validateSignedUrl(value: unknown, expiresAt: unknown) {
  if (typeof value !== "string" || typeof expiresAt !== "string" ||
    !Number.isFinite(Date.parse(expiresAt))) {
    throw new Error("La API devolvio un permiso de archivo invalido.");
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("La API devolvio una URL de archivo invalida.");
  }
  if (url.protocol !== "https:" || url.username || url.password) {
    throw new Error("La API devolvio una URL de archivo invalida.");
  }
  if (Date.parse(expiresAt) <= Date.now()) {
    throw new Error("El permiso del archivo vencio. Intente nuevamente.");
  }
}

function validateUploadPermission(upload: FileUploadResponse["upload"] | undefined, file: File) {
  if (!upload || upload.method !== "PUT" || upload.headers?.["Content-Type"] !== file.type) {
    throw new Error("La API devolvio un permiso de subida invalido.");
  }
  validateSignedUrl(upload.url, upload.expiresAt);
}

export async function requestDownload(requestUrl: string): Promise<FileDownloadResponse> {
  const response = await axiosGet(requestUrl, true);
  if (response?.status !== STATUS_CODES.OK) {
    const message = response?.data?.message;
    throw new Error(typeof message === "string" && message.trim() ? message : "No se pudo solicitar la descarga.");
  }
  const data: FileDownloadResponse | undefined = response.data;
  if (!data) {
    throw new Error("La API devolvio un permiso de descarga invalido.");
  }
  validateSignedUrl(data.url, data.expiresAt);
  return data;
}

export async function readJsonFromStorage(download: FileDownloadResponse): Promise<unknown> {
  validateSignedUrl(download?.url, download?.expiresAt);

  let content: string;
  try {
    const response = await storageClient.get<string>(download.url, { responseType: "text" });
    content = response.data;
  } catch {
    throw new Error("No se pudo leer el archivo JSON desde S3. Revise la conexion y los permisos de lectura.");
  }

  try {
    return JSON.parse(content) as unknown;
  } catch {
    throw new Error("El archivo obtenido de S3 no contiene un JSON valido.");
  }
}

export async function requestUpload(file: File): Promise<FileUploadResponse> {
  validateFile(file);
  const body: FileUploadRequest = {
    originalName: file.name.trim(),
    contentType: file.type,
    sizeBytes: file.size,
  };
  const response = await axiosPost(FILES_UPLOADS_URL, body, true);
  if (response?.status !== STATUS_CODES.OK) {
    throw new Error(response?.data?.message || "No se pudo solicitar el permiso de subida.");
  }
  const data: FileUploadResponse | undefined = response.data;
  if (!data || !Number.isSafeInteger(data.fileId) || data.fileId <= 0 ||
    data.status !== "pending" || typeof data.key !== "string" || !data.key) {
    throw new Error("La API devolvio una respuesta de subida invalida.");
  }
  validateUploadPermission(data.upload, file);
  return data;
}

export async function uploadToStorage(
  file: File,
  upload: FileUploadResponse["upload"],
  onProgress?: (progress: number) => void,
): Promise<void> {
  validateFile(file);
  validateUploadPermission(upload, file);
  try {
    await storageClient.put(upload.url, file, {
      headers: { "Content-Type": upload.headers["Content-Type"] },
      onUploadProgress: onProgress ? (event) => {
        const total = event.total || file.size;
        onProgress(Math.max(0, Math.min(100, Math.floor(event.loaded * 100 / total))));
      } : undefined,
    });
  } catch {
    // Do not expose the signed URL or AWS response through the error object.
    throw new Error("No se pudo subir el archivo a S3. Revise la conexion y los permisos de carga.");
  }
}

export async function confirmUpload(fileId: number): Promise<ConfirmedFile> {
  if (!Number.isSafeInteger(fileId) || fileId <= 0) {
    throw new Error("El identificador del archivo no es valido.");
  }
  const response = await axiosPost(getFileConfirmUrl(fileId), undefined, true);
  if (response?.status !== STATUS_CODES.OK) {
    throw new Error(response?.data?.message || "No se pudo confirmar el archivo.");
  }
  const data: ConfirmedFile | undefined = response.data;
  if (!data || data.fileId !== fileId || data.status !== "ready" ||
    typeof data.originalName !== "string" || !data.originalName.trim() ||
    !FILE_UPLOAD_CONTENT_TYPES.some((type) => type === data.contentType) ||
    !Number.isSafeInteger(data.sizeBytes) || data.sizeBytes <= 0 || data.sizeBytes > FILE_UPLOAD_MAX_SIZE_BYTES) {
    throw new Error("La API devolvio una confirmacion de archivo invalida.");
  }
  return data;
}
